// data/videoLibrary.js — training / help videos shown on the ProfileHome shelf.
//
// Shaped like accessMap.js: every video is declared ONCE, rows are composed from those
// declarations by reference, and the export is keyed by tier. A user resolves to exactly
// ONE entry in that map — never a union of everything they qualify for.
//
// That is the difference from the old version. It listed shelves with `sections: [...]`
// and showed you every shelf whose section you could reach, so 'IT Operations' — which sits
// in the IT, management, admin, supervisor, execute, IGLU and client role lists — collected
// a separate stacked row for each one. Now IT has a single line in VIDEO_MAP saying what IT
// sees, and changing it changes nothing else.
//
// ─────────────────────────────────────────────────────────────────────────────
// To add a video: add it to VIDEOS, then reference it from a row below.
//   url     YouTube link (youtu.be/… or youtube.com/watch?v=…) — anything VideoPlayer takes
//   image   optional thumbnail; omit and the YouTube thumbnail is used
//   minutes optional, shown as a small "3 min" tag on the card
//   requires optional gate, same keys as map.js itemVisible (e.g. 'casual')
//
// To make a video common to all SNL staff: add it to COMMON. It leads the row for every
// internal tier, once, with no per-tier copy to keep in step. IGLU and clients deliberately
// don't include COMMON (see VIDEO_MAP) — they only ever see their own videos.
// ─────────────────────────────────────────────────────────────────────────────

const VIDEOS = {
  workflow:      { id: 'workflow',       title: 'Using Workflow',       summary: 'Periodicals, tickets and updates on your phone.',            image: '/static/images/companypic.png', minutes: 3 },   // no url = coming soon
  onboarding:    { id: 'onboarding',     title: 'Onboarding process',   summary: 'From invitation to your first shift — what to fill in and where.', url: 'https://youtu.be/nfipkklavuI', minutes: 4 },
  availability:  { id: 'availability',   title: 'Extra Work Availability Staff',    summary: 'Tell supervisors when and where you can work.',              url: 'https://youtu.be/1iXzuv7SqXA', requires: 'casual' },
  answerOffer:      { id: 'answer-offer',      title: 'My Availability Staff',     summary: 'Accept or decline from Extra work, and what happens next.',   url: 'https://youtu.be/BwSk7CgSC4Q', minutes: 4 },
  assign1:        { id: 'assign1',         title: 'Assigning casual jobs Part 1', summary: 'Open Slots → Assign → Responses → Roster.',                 url: 'https://youtu.be/hhpCoVIavtQ', minutes: 3 },
  assign2:        { id: 'assign2',         title: 'Assigning casual jobs Part 2', summary: 'Open Slots → Assign → Responses → Roster.',                 url: 'https://youtu.be/189GIPw9_3w', minutes: 3 },
  messages:      { id: 'messages',       title: 'Global Messaging system SNL',      summary: 'Broadcast to a group or message one person from the bell.',  url: 'https://youtu.be/t6XOiwYtyUA' },
  compliance:    { id: 'compliance',     title: 'User compliance',      summary: 'Expiry alerts, contracts and ABN checks.',                   image: '/static/images/companypic.png', minutes: 3 },   // no url = coming soon
  activation:    { id: 'activation',     title: 'Staff activation',     summary: 'Final activation of a new subcontractor.',                   image: '/static/images/companypic.png', minutes: 3 },   // no url = coming soon
  igluTickets:   { id: 'iglu-message',   title: 'Global Messaging system Iglu',     summary: 'Broadcast to a group or message one person from the bell.',                       url: 'https://youtu.be/rJcwq_nF0Q4' },
  clientTickets: { id: 'client-message', title: 'Global Messaging system Clients',     summary: 'Broadcast to a group or message one person from the bell.',                       url: 'https://youtu.be/rJcwq_nF0Q4' },
};

// ── groups ───────────────────────────────────────────────────────────────────
// Named once, reused by reference — the accountEntryDesktop trick from accessMap.
const COMMON     = [VIDEOS.messages,VIDEOS.availability,VIDEOS.answerOffer,VIDEOS.onboarding];// every INTERNAL tier (not IGLU / clients)
const STAFF      = [VIDEOS.workflow];
const SUPERVISOR = [VIDEOS.assign1,VIDEOS.assign2];
const MANAGEMENT = [VIDEOS.compliance, VIDEOS.activation];
const IT         = [];                                             // nothing IT-only yet
const IGLU       = [VIDEOS.igluTickets];
const CLIENT     = [VIDEOS.clientTickets];

// The single row everything lands in. One heading, one horizontal strip, no stacking.
const ROW_HEADING = 'Getting started';

// ── tiers ────────────────────────────────────────────────────────────────────
// Each tier lists the groups that make up its ONE row, in the order they appear left to
// right: COMMON first, then the viewer's own level, then everything else they should see.
// Senior internal tiers (it / management / admin) run the whole catalogue, so they can find
// any video a member of staff or a client might ask them about. Supervisor and execute stop
// at their own level — a supervisor never picks up management content. IGLU and clients are
// external: their lines are their own group only, deliberately without COMMON.
// Duplicates across groups are dropped, so a video can sit in COMMON and a tier group
// without showing twice.
export const VIDEO_MAP = {
  it:         [COMMON, IT, MANAGEMENT, SUPERVISOR, STAFF],
  management: [COMMON, MANAGEMENT, SUPERVISOR, IT, STAFF],
  admin:      [COMMON, MANAGEMENT, SUPERVISOR, STAFF],
  supervisor: [COMMON, SUPERVISOR, STAFF],
  execute:    [COMMON, STAFF],
  iglu:       [IGLU],
  client:     [CLIENT],
};

// ── helpers ──────────────────────────────────────────────────────────────────
const YT_ID = /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/;
export const youtubeId = (url = '') => (url.match(YT_ID) || [])[1] || '';
export const youtubeThumb = (url) => { const id = youtubeId(url); return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : ''; };
export const DEFAULT_THUMB = '/static/images/workflow_app_screen.jpg?v=1';
/**
 * The ONE tier this user resolves to. Same precedence as getRoleAccent in map.js — group
 * before job, then most senior job first — so the shelf and the accent colour can never
 * disagree about who someone is. `hasSectionAccess` is injected rather than imported so
 * this file still has no dependency on map.js and can't form a cycle.
 */
export const tierForUser = (user, hasSectionAccess) => {
  if (!user) return null;
  if (user.group === 'IGLU South Yarra') return 'iglu';
  if (user.group === 'Client') return 'client';
  // 'IT Operations' is deliberately present in the IGLU and client role lists (a testing
  // backdoor in map.js), so job tiers are only checked after the group tiers above.
  return ['it', 'management', 'admin', 'supervisor', 'execute'].find((t) => hasSectionAccess(t, user)) || null;
};

/**
 * The user's videos as a SINGLE row, so the shelf scrolls horizontally instead of stacking
 * a row per level. Groups are concatenated in the order VIDEO_MAP lists them, videos gated
 * by `requires` drop out via itemVisible, and anything appearing in two groups is kept once
 * at its first position. Returns [] (VideoShelf renders nothing) when there's nothing to show.
 *
 * Still an array of rows, because that is what VideoShelf takes — there is just one of them.
 */
export const shelvesForUser = (user, { hasSectionAccess, itemVisible }) => {
  const tier = tierForUser(user, hasSectionAccess);
  const groups = (tier && VIDEO_MAP[tier]) || [];
  const seen = new Set();
  const videos = [];
  for (const group of groups) {
    for (const v of group) {
      if (!itemVisible(v, user) || seen.has(v.id)) continue;
      seen.add(v.id);
      videos.push(v);
    }
  }
  return videos.length ? [{ id: 'videos', heading: ROW_HEADING, videos }] : [];
};
